import {
  Grid,
  WithStyles,
  withStyles,
  Typography,
  Theme,
  CircularProgress
} from '@material-ui/core'
import moment, { Moment } from 'moment-timezone'
import { Fragment, useEffect, useState } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { formValueSelector } from 'redux-form'
import { closeDialog, openDialog } from '../../../../../actions/dialogs'
import { deleteShowingFromFieldArray } from '../../../../../actions/showings'
import { OpenDialog, CloseDialog } from '../../../../../actions/types'
import ErrorMessage from '../../../../../components/Error/ErrorMessage'
import { selectApartmentById } from '../../../../../selectors/apartments'
import {
  selectAvailabilitiesByApartment,
  selectAvailabilitiesByApplicationIds
} from '../../../../../selectors/applicantAvailability'
import { selectSelectedApplications } from '../../../../../selectors/applications'
import { selectContentIdFromPathname } from '../../../../../selectors/router'
import { selectShowingsInterfaceByApartmentId } from '../../../../../selectors/showings'
import { Apartment } from '../../../../../types/apartments'
import { ApplicantAvailability } from '../../../../../types/applicantAvailability'
import {
  AddShowingsFormData,
  FormShowing,
  Showing
} from '../../../../../types/showings'
import {
  EmptyShowingsInterface,
  StateInterface
} from '../../../../../types/states'
import {
  getAvailabilitesFromSpesificDate,
  getAvailabilitesOnSpesificTime,
  getAvailabilitiesOnSpesificDate
} from '../../../../../utils/applicantAvailabilities'
import {
  arrayContainsTime,
  objectArrayContainsTime
} from '../../../../../utils/dates'
import { bindSubmitActionToPromise } from '../../../../../utils/forms'
import {
  getShowingsOnSpesificDate,
  getShowingsTimesFromSpesificDate
} from '../../../../../utils/showings'
import { getFutureShowings } from '../../../../../utils/showings/filterAndSearch'

const styles = ({ palette }: Theme) => ({
  container: {
    'box-sizing': 'border-box',
    background: '#E7ECEF',
    borderRadius: '27px',
    padding: '20px 15px 20px 15px'
  },
  heading: {
    textAlign: 'center' as const,
    padding: '.5rem 0rem',
    color: palette.grey[300]
  },
  timeButton: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '7px',
    padding: '9px 0',
    cursor: 'pointer',
    background: '#ffffff',
    border: '2px solid #ffffff'
  },
  selectedTimeButton: {
    border: '2px solid #4F9B44',
    background: '#4F9B44',
    color: '#fff'
  },
  selectedExistingTimeButton: {
    border: '2px solid #4F9B44',
    background: '#4F9B44',
    color: '#fff'
  },
  existingTimeButton: {
    border: '2px solid #4F9B44',
    color: '#4F9B44'
  },
  customTimeButton: {
    borderRadius: '4px',
    border: '1px solid #000000',
    textAlign: 'center' as const,
    padding: '8px',
    cursor: 'pointer',
    marginTop: '0.5rem'
  },
  timePicker: {
    'box-sizing': 'border-box',
    borderRadius: '7px',
    width: '100%'
  },
  submitCustomTimeButton: {
    borderRadius: '4px',
    textAlign: 'center' as const,
    padding: '8px 0',
    marginTop: '0.5rem',
    color: '#ffffff',
    background: palette.primary.main
  },
  error: {
    textAlign: 'center' as const,
    color: palette.error.main
  },
  suggestedBox: {
    position: 'absolute' as const,
    right: '-4px',
    top: '-4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.primary.main,
    color: '#fff',
    height: '12px',
    width: '12px',
    borderRadius: '4px',
    border: '1px solid #fff'
  },
  suggestedBoxText: {
    fontSize: '0.6rem'
  },
  selectedTime: {
    border: '2px solid #4F9B44',
    background: '#4F9B44',
    color: '#fff'
    // 'pointer-events': 'none'
  }
})

interface TimePickTableProps {
  input: any
  meta: any
  label: string
  custom: any
  error: any
  submittingLoad: boolean
  activeDates?: string[]
  handleSubmit: any
  addShowingToFieldArray: (formData: AddShowingsFormData) => void
}

interface StateProps {
  apartment?: Apartment
  existingShowings: Showing[]
  showings: Showing[]
  dateValueFromForm: Moment
  availabilities?: ApplicantAvailability[]
}

interface DispatchProps {
  openDialog: OpenDialog
  closeDialog: CloseDialog
  deleteShowingFromFieldArray: (formData: {
    startDate: string
    showings: FormShowing[]
  }) => Promise<unknown>
}

type ComponentProps = TimePickTableProps &
  WithTranslation &
  WithStyles &
  StateProps &
  DispatchProps

const TimePickTable = ({
  input: { onChange },
  classes,
  t,
  apartment,
  openDialog,
  closeDialog,
  existingShowings,
  dateValueFromForm,
  error,
  handleSubmit,
  deleteShowingFromFieldArray,
  addShowingToFieldArray,
  showings,
  activeDates,
  availabilities,
  submittingLoad
}: ComponentProps) => {
  const [selected, setSelected] = useState<string>('')
  const dateTime = moment().set('hour', 8).set('minute', 30)
  const cellCount = 25
  const existingTimes = getShowingsTimesFromSpesificDate(
    showings,
    dateValueFromForm
  )
  const existingAvailabilities = getAvailabilitesFromSpesificDate(
    availabilities ? availabilities : [],
    dateValueFromForm
  )
  const possibleShowingsOnDate = getShowingsOnSpesificDate(
    showings,
    dateValueFromForm
  )
  const isExistingAvailablitiesTime = (time: Moment): boolean =>
    objectArrayContainsTime(existingAvailabilities, time)
  const isExistingShowingTime = (time: Moment): boolean =>
    arrayContainsTime(existingTimes, time)

  const getTimeArray = (): Moment[] => {
    const array = []
    array[0] = dateTime

    /** Create basic timeArray with 30min separation */
    for (let i = 0; i < cellCount; i++) {
      let newTime: any
      if (i !== 0) {
        newTime = moment(dateTime).add(30 * i, 'minute')
      } else {
        newTime = dateTime
      }
      array[i] = newTime
    }

    return array
  }

  const getClassForTime = (time: Moment): string => {
    if (isExistingAvailablitiesTime(time) && time.format('H:mm') === selected) {
      return classes.selectedExistingTimeButton
    } else if (isExistingAvailablitiesTime(time)) {
      return classes.existingTimeButton
    } else if (time.format('H:mm') === selected) {
      return classes.selectedTimeButton
    } else {
      return classes.timeButton
    }
  }
  useEffect(() => {
    if (error) {
      setSelected('')
    }
  }, [error])
  const getAvailabilities = (time: Moment): any => {
    if (!availabilities) {
      return
    }
    const availabilitiesOnSelectedDate = getAvailabilitiesOnSpesificDate(
      availabilities,
      dateValueFromForm
    )
    const availabilitiesOnSelectedTime = getAvailabilitesOnSpesificTime(
      availabilitiesOnSelectedDate,
      time
    )
    if (availabilitiesOnSelectedTime.length > 0) {
      return (
        <div className={classes.suggestedBox}>
          <Typography variant='body1' className={classes.suggestedBoxText}>
            {availabilitiesOnSelectedTime.length}
          </Typography>
        </div>
      )
    }
  }

  const handleOnChange = (time: any): void => {
    if (possibleShowingsOnDate && possibleShowingsOnDate?.length !== 0) {
      possibleShowingsOnDate.map((el) => {
        if (
          moment(el.startDate).format('H:mm') === moment(time).format('H:mm') &&
          isExistingShowingTime(time)
        ) {
          setSelected('')
          deleteShowingFromFieldArray({ startDate: el.startDate, showings })
          return
        }
      })
      if (!isExistingShowingTime(time)) {
        setSelected(time.format('H:mm'))
        onChange(time)
        setTimeout(handleSubmit(addShowingToFieldArray))
      }
    } else {
      setSelected(time.format('H:mm'))
      onChange(time)
      setTimeout(handleSubmit(addShowingToFieldArray))
    }
  }

  const handleGetIsSelectedDate = (time: Moment) => {
    if (
      Array.isArray(activeDates) &&
      activeDates.length > 0 &&
      dateValueFromForm
    ) {
      const result = activeDates.some(
        (element) =>
          `${dateValueFromForm.format('DD')}-${time.format('HH:mm')}` ===
          moment(element).format('DD-HH:mm')
      )
      return result ? classes.selectedTime : ''
    }
    return ''
  }
  useEffect(() => {
    if (Array.isArray(activeDates)) {
      if (
        activeDates.some(
          (element) => selected === moment(element).format('HH:mm')
        )
      ) {
        setSelected('')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDates])
  return (
    <Fragment>
      <div className={classes.container}>
        {error && (
          <Typography align='center' style={{ padding: '15px 0 10px 0' }}>
            <ErrorMessage error={error} />
          </Typography>
        )}

        <Grid container spacing={1}>
          {getTimeArray().map((time, index) => {
            return (
              <Grid key={index} item style={{ width: '20%' }}>
                {time && (
                  <div
                    className={`${classes.timeButton} ${getClassForTime(
                      time
                    )} ${handleGetIsSelectedDate(time)}`}
                    onClick={() => {
                      handleOnChange(
                        moment(time, 'YYYY-MM-DDTHH:mm:ss.SSSZ')
                          .seconds(0)
                          .milliseconds(0)
                      )
                    }}
                  >
                    <Typography variant='button'>
                      {submittingLoad && time.format('H:mm') === selected ? (
                        <Grid container>
                          <CircularProgress
                            size={24.5}
                            style={{
                              color: '#fff'
                            }}
                          />
                        </Grid>
                      ) : (
                        <> {time.format('H:mm')}</>
                      )}
                    </Typography>
                    {getAvailabilities(time)}
                  </div>
                )}
              </Grid>
            )
          })}
        </Grid>
      </div>
    </Fragment>
  )
}
const selector = formValueSelector('CreateShowingsForm')
const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)
  const showingsInterface = apartmentId
    ? selectShowingsInterfaceByApartmentId(apartmentId)(state)
    : EmptyShowingsInterface
  const dateValueFromForm = formValueSelector('AddShowingsForm')(state, 'date')
  const availabilities = apartmentId
    ? selectAvailabilitiesByApartment(apartmentId)(state)
    : undefined
  const selectedApplications = selectSelectedApplications(state)

  return {
    apartment: apartmentId
      ? selectApartmentById(apartmentId)(state)
      : undefined,
    existingShowings: getFutureShowings(showingsInterface.showings, 'reserve'),

    showings: selector(state, 'showings'),
    dateValueFromForm,
    availabilities:
      availabilities && selectedApplications
        ? selectAvailabilitiesByApplicationIds(
            availabilities,
            selectedApplications
          )
        : undefined
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openDialog: (id: string) => dispatch(openDialog(id)),
  closeDialog: (id: string) => dispatch(closeDialog(id)),
  deleteShowingFromFieldArray: bindSubmitActionToPromise(
    dispatch,
    deleteShowingFromFieldArray
  )
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withStyles(styles)(TimePickTable)))
